import { ApiResponse } from '../../types/commons';
import { UserRole } from '../../types/user-role';
import { normalizeApiErrors } from './api-errors.util';
import { HttpStatus } from './http-status.util';
import { SUPERADMIN_AUDIT_UNAVAILABLE_MESSAGE } from './superadmin-audit-unavailable.util';

/** Stable backend codes for User Management. Public contract; key copy on these, never on `errors`. */
export enum UserManagementErrorCode {
  SuperAdminRoleNotAssignable = 'SUPERADMIN_ROLE_NOT_ASSIGNABLE',
  RoleChangeSuperAdminOnly = 'ROLE_CHANGE_SUPERADMIN_ONLY',
  CompanyChangeSuperAdminOnly = 'COMPANY_CHANGE_SUPERADMIN_ONLY',
  CompanyOutOfScope = 'COMPANY_OUT_OF_SCOPE',
  CannotDeleteSelf = 'CANNOT_DELETE_SELF',
  /**
   * No longer returned for Admin-on-Admin (P.O., 2026-10-20: an Admin may edit a
   * fellow Admin's name and email). The backend keeps it for other actors, so the
   * copy stays.
   */
  CannotEditSameOrHigherRole = 'CANNOT_EDIT_SAME_OR_HIGHER_ROLE',
  /** Self, a peer Admin or any other disallowed pairing (P.O., 2026-10-27). The UI keeps that email read-only, so this is a forged or stale request. */
  EmailChangeNotPermitted = 'EMAIL_CHANGE_NOT_PERMITTED',
}

export type UserManagementAction = 'create' | 'update' | 'delete';
/** `emailTaken` is shown on the Email field itself, not as a toast. */
export type UserManagementErrorPresentation = 'toast' | 'unavailable' | 'emailTaken';

export interface UserManagementFailure {
  presentation: UserManagementErrorPresentation;
  message: string;
  errorCode: string | null;
  statusCode: number;
}

interface CodeCopy {
  presentation: UserManagementErrorPresentation;
  message: string;
}

const COPY_BY_CODE: Record<UserManagementErrorCode, CodeCopy> = {
  [UserManagementErrorCode.SuperAdminRoleNotAssignable]: { presentation: 'toast', message: 'That role cannot be assigned.' },
  [UserManagementErrorCode.RoleChangeSuperAdminOnly]: { presentation: 'toast', message: 'Only a Superadmin can change a role.' },
  [UserManagementErrorCode.CompanyChangeSuperAdminOnly]: { presentation: 'toast', message: 'Only a Superadmin can change a company.' },
  [UserManagementErrorCode.CompanyOutOfScope]: { presentation: 'toast', message: 'You can only add users to your own company.' },
  [UserManagementErrorCode.CannotDeleteSelf]: { presentation: 'toast', message: 'You cannot delete your own account.' },
  [UserManagementErrorCode.CannotEditSameOrHigherRole]: { presentation: 'toast', message: 'You cannot edit a user at your own role level or above.' },
  [UserManagementErrorCode.EmailChangeNotPermitted]: { presentation: 'toast', message: "You are not allowed to change this user's email." },
};

/**
 * The backend answers a duplicate email on `update-user` with a 400, the text
 * below and NO `errorCode` (P.O. may ask for one later). Matching the text is the
 * only signal today, so it is confined to this one constant. It is the rare
 * race: Save clicked before the live `email-availability` check disabled it.
 */
const EMAIL_IN_USE_SERVER_TEXT = 'email is already in use.';
const EMAIL_IN_USE_MESSAGE = 'This email is already in use. Please use a different email.';

const GENERIC_MESSAGE_BY_ACTION: Record<UserManagementAction, string> = {
  create: 'There was an issue adding the user. Please try again later.',
  update: 'There was an error updating the user. Please try again later.',
  delete: 'There was an issue deleting the user. Please try again later.',
};

const USER_GONE_MESSAGE = 'This user no longer exists. The list has been refreshed.';

/** Who is asking, so one code can show the server's own text to the role that can act on it. */
export interface UserManagementFailureOptions {
  callerRole?: UserRole | null;
}

/**
 * Turns a failed User Management response into a presentation decision.
 * Pure: the component decides how to render it. The copy is keyed on `errorCode`
 * and status. The one exception: for `COMPANY_OUT_OF_SCOPE` a SuperAdmin sees the
 * server's first `errors` entry as is, because it tells them how to restore a
 * deleted account in its original company; an Admin keeps our neutral copy.
 */
export function resolveUserManagementFailure(
  response: Pick<ApiResponse, 'statusCode' | 'errorCode'> & Partial<Pick<ApiResponse, 'errors'>>,
  action: UserManagementAction,
  options: UserManagementFailureOptions = {},
): UserManagementFailure {
  const errorCode = typeof response.errorCode === 'string' && response.errorCode.length > 0
    ? response.errorCode
    : null;
  const statusCode = response.statusCode;
  // Own-property check: a plain-object lookup would also answer for inherited
  // keys such as "constructor" and return a function instead of copy.
  const known = errorCode !== null && Object.hasOwn(COPY_BY_CODE, errorCode)
    ? COPY_BY_CODE[errorCode as UserManagementErrorCode]
    : undefined;

  if (known && errorCode === UserManagementErrorCode.CompanyOutOfScope && options.callerRole === UserRole.SuperAdmin) {
    // `errors` may be a list or a bare string; normalizeApiErrors flattens both.
    const [serverText] = normalizeApiErrors(response.errors);
    if (typeof serverText === 'string' && serverText.trim().length > 0) {
      return { ...known, message: serverText, errorCode, statusCode };
    }
  }
  if (known) {
    return { ...known, errorCode, statusCode };
  }
  if (
    action === 'update' &&
    statusCode === HttpStatus.BadRequest &&
    normalizeApiErrors(response.errors).some(
      (text) => text.trim().toLowerCase() === EMAIL_IN_USE_SERVER_TEXT,
    )
  ) {
    return { presentation: 'emailTaken', message: EMAIL_IN_USE_MESSAGE, errorCode, statusCode };
  }
  if (statusCode === HttpStatus.ServiceUnavailable) {
    return { presentation: 'unavailable', message: SUPERADMIN_AUDIT_UNAVAILABLE_MESSAGE, errorCode, statusCode };
  }
  if (statusCode === HttpStatus.NotFound) {
    return { presentation: 'toast', message: USER_GONE_MESSAGE, errorCode, statusCode };
  }
  // Unknown code / empty body: generic per-action copy.
  return { presentation: 'toast', message: GENERIC_MESSAGE_BY_ACTION[action], errorCode, statusCode };
}
